import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Table, TableHeader, TableBody, TableRow, TableCell, SortIndicator } from '@/components/ui/Table';
import Icon from '@/components/ui/Icon';
import Spinner from '@/components/common/Spinner';
import { LinkButton } from '@/components/ui/Buttons';
import ExportCsvButton from './ExportCsvButton';
import columns from './columns';
import type { Transaction } from './types';

interface OrderTableProps {
  title: string;
  data: Transaction[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const OrderTable = ({ title, data, loading, error, onRefresh }: OrderTableProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  const csvFilename = title.replace(/\s+/g, '_').toLowerCase();

  return (
    <div className="tw-mb-8">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        <h2 className="tw-text-lg tw-font-semibold">
          {title} ({loading ? '...' : data.length} rows)
        </h2>
        <LinkButton onClick={onRefresh}>
          <Icon fill="currentColor" icon="Refresh" size={18} />
        </LinkButton>
        <ExportCsvButton data={data} filename={csvFilename} />
      </div>

      {error && <div className="tw-text-red-600 tw-mb-2">Error: {error}</div>}

      {loading ? (
        <Spinner />
      ) : (
        <div className="tw-overflow-x-auto">
          <Table className="tw-w-full tw-text-sm tw-border-collapse">
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableCell
                      key={header.id}
                      header
                      className={[
                        'tw-border tw-border-gray-300 tw-bg-gray-100',
                        'tw-text-left tw-whitespace-nowrap',
                        'tw-cursor-pointer tw-select-none',
                      ].join(' ')}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIndicator direction={header.column.getIsSorted()} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow key={row.id} className="hover:tw-bg-gray-50">
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className="tw-border tw-border-gray-200 tw-whitespace-nowrap"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default OrderTable;
